/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Inject, Injectable, NgZone} from '@angular/core';

import {getDOM} from '../dom_adapter';
import {DOCUMENT} from '../dom_tokens';

import {EventManagerPlugin} from './event_manager';

const /** @type {?} */ MODIFIER_KEYS = ['alt', 'control', 'meta', 'shift'];
const /** @type {?} */ MODIFIER_KEY_GETTERS: {[key: string]: (event: KeyboardEvent) => boolean} = {
  'alt': (event: KeyboardEvent) => event.altKey,
  'control': (event: KeyboardEvent) => event.ctrlKey,
  'meta': (event: KeyboardEvent) => event.metaKey,
  'shift': (event: KeyboardEvent) => event.shiftKey
};
/**
 * \@experimental
 */
export class KeyEventsPlugin extends EventManagerPlugin {
/**
 * @param {?} doc
 */
constructor( doc: any) { super(doc); }
/**
 * @param {?} eventName
 * @return {?}
 */
supports(eventName: string): boolean { return KeyEventsPlugin.parseEventName(eventName) != null; }
/**
 * @param {?} element
 * @param {?} eventName
 * @param {?} handler
 * @return {?}
 */
addEventListener(element: HTMLElement, eventName: string, handler: Function): Function {
    const /** @type {?} */ parsedEvent = /** @type {?} */(( KeyEventsPlugin.parseEventName(eventName)));

    const /** @type {?} */ outsideHandler =
        KeyEventsPlugin.eventCallback(parsedEvent['fullKey'], handler, this.manager.getZone());

    return this.manager.getZone().runOutsideAngular(() => {
      return getDOM().onAndCancel(element, parsedEvent['domEventName'], outsideHandler);
    });
  }
/**
 * @param {?} eventName
 * @return {?}
 */
static parseEventName(eventName: string): {[key: string]: string}|null {
    const /** @type {?} */ parts: string[] = eventName.toLowerCase().split('.');

    const /** @type {?} */ domEventName = parts.shift();
    if ((parts.length === 0) || !(domEventName === 'keydown' || domEventName === 'keyup')) {
      return null;
    }

    const /** @type {?} */ key = KeyEventsPlugin._normalizeKey( /** @type {?} */((parts.pop())));

    let /** @type {?} */ fullKey = '';
    MODIFIER_KEYS.forEach(modifierName => {
      const /** @type {?} */ index: number = parts.indexOf(modifierName);
      if (index > -1) {
        parts.splice(index, 1);
        fullKey += modifierName + '.';
      }
    });
    fullKey += key;

    if (parts.length != 0 || key.length === 0) {
      // returning null instead of throwing to let another plugin process the event
      return null;
    }

    const /** @type {?} */ result: {[k: string]: string} = {};
    result['domEventName'] = domEventName;
    result['fullKey'] = fullKey;
    return result;
  }
/**
 * @param {?} event
 * @return {?}
 */
static getEventFullKey(event: KeyboardEvent): string {
    let /** @type {?} */ fullKey = '';
    let /** @type {?} */ key = getDOM().getEventKey(event);
    key = key.toLowerCase();
    if (key === ' ') {
      key = 'space';  // for readability
    } else if (key === '.') {
      key = 'dot';  // because '.' is used as a separator in event names
    }
    MODIFIER_KEYS.forEach(modifierName => {
      if (modifierName != key) {
        const /** @type {?} */ modifierGetter = MODIFIER_KEY_GETTERS[modifierName];
        if (modifierGetter(event)) {
          fullKey += modifierName + '.';
        }
      }
    });
    fullKey += key;
    return fullKey;
  }
/**
 * @param {?} fullKey
 * @param {?} handler
 * @param {?} zone
 * @return {?}
 */
static eventCallback(fullKey: any, handler: Function, zone: NgZone): Function {
    return (event: any /** TODO #9100 */) => {
      if (KeyEventsPlugin.getEventFullKey(event) === fullKey) {
        zone.runGuarded(() => handler(event));
      }
    };
  }
/**
 * \@internal
 * @param {?} keyName
 * @return {?}
 */
static _normalizeKey(keyName: string): string {
    // TODO: switch to a Map if the mapping grows too much
    switch (keyName) {
      case 'esc':
        return 'escape';
      default:
        return keyName;
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: undefined, decorators: [{ type: Inject, args: [DOCUMENT, ] }, ]},
];
}

function KeyEventsPlugin_tsickle_Closure_declarations() {
/** @type {?} */
KeyEventsPlugin.decorators;
/**
 * @nocollapse
 * @type {?}
 */
KeyEventsPlugin.ctorParameters;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
